import React, { useEffect, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  ChevronDown,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Plus,
  Settings,
  Shield,
  Sun,
} from 'lucide-react';
import { clientId } from '../config/cognito-config';
import { cognitoDomain, cognitoAuthConfig } from '../config/oidc-config';
import { useOrganization } from '../contexts/OrganizationContext';
import { useUser } from '../contexts/UserContext';
import { useTheme, ThemePreference } from '../contexts/ThemeContext';
import { noteOrganizationSwitch } from '../hooks/useInfiniteList';
import { usePermissions } from '../hooks/usePermissions';
import { ROUTES, RouteMeta, findRouteMeta, isRouteActive } from '../routes';
import CreateOrganizationModal from './CreateOrganizationModal';
import NoOrganizationAccess from '../pages/NoOrganizationAccess';
import { DisclosureChevron } from './DisclosureChevron';
import { ErrorState } from './ErrorState';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Separator } from './ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './ui/sheet';
import { cn, FOCUS_RING_ITEM } from '../lib/utils';

const APP_NAME = 'Dory Portal';

/**
 * Best-effort cleanup of any legacy amazon-cognito-identity-js session.
 *
 * That library used to be a dependency purely so logout could call
 * `userPool.getCurrentUser()?.signOut()`, which does nothing more than delete
 * the `CognitoIdentityServiceProvider.<clientId>.*` keys it writes into
 * localStorage. This portal has never written them — sign-in goes through
 * oidc-client-ts and the hosted UI — so the call was already a no-op for any
 * current user, but it is kept (dependency-free) for anyone whose browser
 * still holds keys from an older build.
 */
function clearLegacyCognitoSession(): void {
  try {
    const prefix = `CognitoIdentityServiceProvider.${clientId}.`;
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith(prefix)) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // Private mode / storage disabled — nothing to clean up.
  }
}

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * Sidebar nav item — a real link, so it stays middle-clickable.
 *
 * Deliberately `Link`, not `NavLink`. `isRouteActive` (src/routes.ts) is the one
 * source of truth for which nav item is current, and it encodes rules NavLink's
 * own matcher does not have — notably that a parent is NOT current while an
 * indented child matches exactly, so `/my-org` goes quiet on `/my-org/roles`.
 *
 * NavLink overrode that. It resolves `aria-current` as
 *
 *     "aria-current": ariaCurrentProp = "page",   // JS default parameter
 *     let ariaCurrent = isActive ? ariaCurrentProp : undefined;
 *
 * so passing `aria-current={undefined}` to mean "not current" instead triggers
 * the default and yields "page", applied whenever NavLink's *own* isActive is
 * true. Without an `end` prop that includes every parent path — so on
 * /my-org/roles both it and /my-org announced themselves as the current page.
 * Two `aria-current="page"` links is a real accessibility defect: a screen
 * reader reports the current location twice, in two different places.
 *
 * Adding `end` would not fix it either, because the app deliberately DOES want
 * the parent active on other /my-org/* paths. The component uses no NavLink
 * feature — the class name is a plain string, there are no render props — so
 * Link is the correct primitive and the app keeps deciding what is active.
 */
const SidebarNavItem: React.FC<{ route: RouteMeta; onNavigate: () => void }> = ({
  route,
  onNavigate,
}) => {
  const location = useLocation();
  const active = isRouteActive(route, location.pathname);
  const Icon = route.icon;
  const isAdmin = route.nav === 'admin';

  return (
    <Link
      to={route.path}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        FOCUS_RING_ITEM,
        route.indent && 'pl-9',
        active
          ? 'bg-primary/10 text-primary'
          : isAdmin
            ? 'text-warning-emphasis hover:bg-accent'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {Icon ? <Icon aria-hidden="true" className="h-4 w-4 shrink-0" /> : null}
      <span className="truncate">{route.title}</span>
    </Link>
  );
};

/**
 * A collapsible nav group: a heading that opens and closes its children.
 *
 * The header is a BUTTON, not a link. /system-admin itself renders nothing —
 * it only redirects to whichever child the caller may open — so making the
 * heading navigable would give the sidebar a row that lands somewhere other
 * than where it says. Expanded by default, because the group is the reason an
 * administrator opened the sidebar, and collapsing it by default would hide
 * every one of its pages behind a click.
 */
const SidebarNavGroup: React.FC<{
  header: RouteMeta;
  children_: RouteMeta[];
  onNavigate: () => void;
}> = ({ header, children_, onNavigate }) => {
  const location = useLocation();
  const [open, setOpen] = React.useState(true);
  const Icon = header.icon;
  // A child being current while the group is shut would hide the only
  // indication of where the user is, so the open state follows the URL.
  const holdsCurrent = children_.some((child) => isRouteActive(child, location.pathname));
  const expanded = open || holdsCurrent;
  const panelId = 'nav-group-system-admin';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          FOCUS_RING_ITEM,
          'text-warning-emphasis hover:bg-accent',
        )}
      >
        {Icon ? <Icon aria-hidden="true" className="h-4 w-4 shrink-0" /> : null}
        <span className="truncate">{header.title}</span>
        <DisclosureChevron open={expanded} className="ml-auto h-3.5 w-3.5" />
      </button>
      {/*
        The `flex` class is applied ONLY while expanded.
        `hidden` hides an element by way of the UA stylesheet's `display: none`,
        which ANY explicit display rule overrides — so `hidden` alongside a
        permanent `flex` class left the collapsed group fully visible. jsdom
        applies no Tailwind CSS, so a unit test could not see it; the E2E suite
        caught it in a real browser.
      */}
      <div
        id={panelId}
        hidden={!expanded}
        className={cn(expanded && 'flex flex-col gap-0.5')}
      >
        {children_.map((route) => (
          <SidebarNavItem key={route.path} route={route} onNavigate={onNavigate} />
        ))}
      </div>
    </>
  );
};

/** Full sidebar content — shared by the fixed desktop rail and the mobile Sheet. */
const SidebarContent: React.FC<{ onNavigate: () => void }> = ({ onNavigate }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();

  const visible = (route: RouteMeta): boolean =>
    !route.permissions || hasAnyPermission(route.permissions) || route.permissions.every(hasPermission);

  const mainRoutes = ROUTES.filter((route) => route.nav === 'main' && visible(route));
  const adminRoutes = ROUTES.filter((route) => route.nav === 'admin' && visible(route));
  // A collapsible group is the header plus every indented route after it. The
  // header is a heading rather than a link, so it is dropped from the flat list
  // and rendered by SidebarNavGroup instead.
  const adminGroupHeader = adminRoutes.find((route) => route.collapsible);
  const adminChildren = adminRoutes.filter((route) => route.indent);
  const adminFlat = adminRoutes.filter((route) => !route.collapsible && !route.indent);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center gap-2.5 px-5">
        <img src="/favicon.svg" alt="" className="h-7 w-7" />
        <span className="text-base font-semibold tracking-tight">Dory</span>
      </div>
      <Separator />
      <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3">
        {mainRoutes.map((route) => (
          <SidebarNavItem key={route.path} route={route} onNavigate={onNavigate} />
        ))}
        {adminRoutes.length > 0 ? (
          <>
            <Separator className="my-2" />
            {adminGroupHeader && adminChildren.length > 0 ? (
              <SidebarNavGroup
                header={adminGroupHeader}
                children_={adminChildren}
                onNavigate={onNavigate}
              />
            ) : null}
            {adminFlat.map((route) => (
              <SidebarNavItem key={route.path} route={route} onNavigate={onNavigate} />
            ))}
          </>
        ) : null}
      </nav>
      <div className="border-t p-4">
        <p className="text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} Dory. All rights reserved.
        </p>
        <div className="mt-2 flex gap-3">
          <Link
            to="/legal/terms-of-service"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Terms of Service
          </Link>
          <Link
            to="/legal/privacy-policy"
            className="text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { user, avatarInitials, isLoading: userLoading, error: userError } = useUser();
  const {
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    createOrganization,
    isLoading: orgLoading,
    membershipError,
    refreshOrganizations,
  } = useOrganization();
  const { preference, setPreference } = useTheme();
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isFirstRender = useRef(true);
  const wasWithoutOrganization = useRef(false);
  const isFillRoute = findRouteMeta(location.pathname)?.layout === 'fill';

  // Registry-driven document title, one implementation for every page.
  //
  // A path with no registry entry (not-found) is left alone: the page rendered
  // there sets its own title via usePageTitle, and this effect runs after it,
  // so overwriting would undo it.
  //
  // The shell-state dependencies are load-bearing. While the organization list
  // resolves, this component renders NoOrganizationAccess, which claims the
  // title and releases it on unmount — after this effect has already run. Keyed
  // on the pathname alone the title would stay stuck at the bare app name for
  // the rest of the visit, which is what the E2E route smoke matrix caught.
  useEffect(() => {
    const meta = findRouteMeta(location.pathname);
    if (meta) document.title = `${meta.title} — ${APP_NAME}`;
  }, [location.pathname, orgLoading, userLoading, selectedOrganization?.id]);

  // Move focus to the new page's h1 on client-side navigation so screen
  // readers announce the change (skip the initial render).
  //
  // The heading is not there yet when the effect fires: a permission gate and
  // every React.lazy route paint a skeleton first, so a one-shot querySelector
  // found nothing and focus silently stayed on the link the user activated.
  // Wait for the heading across a bounded number of frames instead, and give up
  // rather than steal focus if the user has already moved it into the page.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const main = document.querySelector('main');
    if (!main) return;

    // Bail rather than steal focus if the user has already moved it into the page.
    const focusIsInPage = () =>
      main.contains(document.activeElement) && document.activeElement !== main;

    // Returns whether the job is done. `focus()` is a no-op on an element the
    // browser has not laid out yet, which happens while a lazy route is still
    // settling — so success means the element actually took focus, not merely
    // that focus() was called.
    const focusHeading = (): boolean => {
      if (focusIsInPage()) return true;
      const heading = main.querySelector<HTMLHeadingElement>('h1');
      if (!heading) return false;
      heading.focus({ preventScroll: true });
      return document.activeElement === heading;
    };

    if (focusHeading()) return;

    // The heading is not there yet: a permission gate and every React.lazy
    // route paint a skeleton first, and a lazy chunk can take longer to arrive
    // than any fixed number of frames. Watch for it instead, with a cap so the
    // observer cannot outlive the navigation.
    let frame = 0;
    const observer = new MutationObserver(() => {
      if (focusHeading()) {
        observer.disconnect();
        return;
      }
      // The heading can be in the DOM a frame before it is focusable, and that
      // insertion may be the last mutation — so retry off the observer too.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (focusHeading()) observer.disconnect();
      });
    });
    observer.observe(main, { childList: true, subtree: true });
    const stop = window.setTimeout(() => observer.disconnect(), 5000);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.clearTimeout(stop);
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      // Always clear local OIDC user first.
      await auth.removeUser();
    } catch (error) {
      console.error('Error clearing OIDC user:', error);
    }

    // Best-effort local user pool cleanup.
    clearLegacyCognitoSession();

    // Clear Cognito managed-login session cookie in the browser.
    const logoutUrl = `${cognitoDomain}/logout?client_id=${encodeURIComponent(cognitoAuthConfig.client_id)}&logout_uri=${encodeURIComponent(
      cognitoAuthConfig.post_logout_redirect_uri
    )}`;
    window.location.assign(logoutUrl);
  };

  const handleOrganizationSelect = (orgId: string) => {
    const org = organizations.find((candidate) => candidate.id === orgId);
    if (org) {
      // Before the new organization renders: a list whose page is withdrawn
      // under it must still know, on the way back, that its ?q= is stale.
      if (org.id !== selectedOrganization?.id) noteOrganizationSwitch();
      // Pages react to selectedOrganization from context — no window events.
      setSelectedOrganization(org);
    }
  };

  const handleCreateOrganization = async (name: string, licenseKey: string) => {
    await createOrganization(name, licenseKey);
    setShowCreateOrgModal(false);
  };

  // Refresh on this page calls refreshUser(), which flips userLoading — so the
  // naive condition unmounts the page mid-click and takes its own Refresh
  // button with it, replacing everything with the full-page loading state. Hold
  // the page up while that refresh is in flight, and let the button show its
  // own spinner. The latch only updates once loading settles, so the page comes
  // down exactly when the refresh returns an organization.
  const hasNoOrganizationAccessNow =
    Boolean(user) && !userLoading && !userError && organizations.length === 0;
  if (!userLoading) wasWithoutOrganization.current = hasNoOrganizationAccessNow;

  const hasNoOrganizationAccess =
    hasNoOrganizationAccessNow || (wasWithoutOrganization.current && userLoading && !userError);

  if (hasNoOrganizationAccess) {
    return (
      <>
        <NoOrganizationAccess
          isRefreshing={orgLoading || userLoading}
          onRefresh={refreshOrganizations}
          onSignOut={handleLogout}
          onCreateOrganization={() => setShowCreateOrgModal(true)}
        />
        <CreateOrganizationModal
          isOpen={showCreateOrgModal}
          onClose={() => setShowCreateOrgModal(false)}
          onCreate={handleCreateOrganization}
        />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile drawer: real Sheet — focus trap, Escape, inert when closed. */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0 lg:hidden">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SheetDescription className="sr-only">Pages you can open in this organization</SheetDescription>
          <SidebarContent onNavigate={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Desktop rail. */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 border-r bg-card lg:block">
        <SidebarContent onNavigate={() => undefined} />
      </aside>

      <div className="flex min-h-screen flex-col lg:ml-64">
        <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <Menu aria-hidden="true" className="h-5 w-5" />
            </Button>
            <div className="flex flex-1 items-center justify-end gap-2 sm:gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="min-w-0 max-w-[180px] justify-between sm:min-w-[220px] sm:max-w-none"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      {selectedOrganization?.isSystemOrg ? (
                        <Shield aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-warning-emphasis" />
                      ) : null}
                      <span className="truncate">
                        {selectedOrganization ? selectedOrganization.name : 'Select organization'}
                      </span>
                    </span>
                    <ChevronDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Organization</DropdownMenuLabel>
                  {/* One value from a set, applied immediately — the same
                      radio group Theme uses, so the active organization is
                      announced as checked rather than marked by a decorative
                      icon assistive technology cannot see (DESIGN_SYSTEM.md §4 dropdown-menu, §9). */}
                  <DropdownMenuRadioGroup
                    value={selectedOrganization?.id ?? ''}
                    onValueChange={handleOrganizationSelect}
                  >
                    {organizations.map((org) => (
                      <DropdownMenuRadioItem key={org.id} value={org.id}>
                        <span className="flex min-w-0 items-center gap-1.5">
                          {org.isSystemOrg ? (
                            <Shield aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-warning-emphasis" />
                          ) : null}
                          {/* No role suffix: the legacy membership label grants
                              nothing (rules of record 7). The system organization
                              keeps only its Shield marker. */}
                          <span className="truncate">{org.name}</span>
                        </span>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                  {organizations.length > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuItem
                    onSelect={(event) => {
                      event.preventDefault();
                      setShowCreateOrgModal(true);
                    }}
                  >
                    <Plus aria-hidden="true" className="mr-2 h-4 w-4" />
                    Create new organization
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" aria-label="User menu">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs">{avatarInitials}</AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onSelect={() => navigate('/account-settings')}>
                    <Settings aria-hidden="true" className="mr-2 h-4 w-4" />
                    Account settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Theme</DropdownMenuLabel>
                  <DropdownMenuRadioGroup
                    value={preference}
                    onValueChange={(value) => setPreference(value as ThemePreference)}
                  >
                    {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                      <DropdownMenuRadioItem key={value} value={value}>
                        <Icon aria-hidden="true" className="mr-2 h-4 w-4" />
                        {label}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleLogout}>
                    <LogOut aria-hidden="true" className="mr-2 h-4 w-4" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* A 'fill' route is a fixed-height column at lg: its list's table
            region is the only scroller, so search and the header stay put and
            the horizontal scrollbar stays on screen (DESIGN_SYSTEM.md §5 Lists).
            h-[calc(100dvh-4rem)] is the viewport under the h-16 sticky header.
            lg:flex-none is load-bearing: main sits in a min-h-screen flex
            column, where flex-1's 0% basis would override the height and let
            main grow with its rows — the region would never be bounded and
            every batch would auto-load (caught by e2e/specs/infinite-scroll). */}
        <main
          className={cn(
            'flex-1 p-6',
            isFillRoute && 'lg:flex lg:h-[calc(100dvh-4rem)] lg:min-h-0 lg:flex-none lg:flex-col lg:overflow-hidden print:h-auto print:overflow-visible',
          )}
          data-layout={isFillRoute ? 'fill' : undefined}
        >
          {/* Membership failures only. App-client failures belong to the pages
              that use app clients, and a dialog's failure stays in the dialog. */}
          {membershipError ? (
            <ErrorState
              title="Couldn't load your organizations"
              error={membershipError}
              onRetry={refreshOrganizations}
              className="mb-6 shrink-0"
            />
          ) : null}
          <Outlet />
        </main>
      </div>

      <CreateOrganizationModal
        isOpen={showCreateOrgModal}
        onClose={() => setShowCreateOrgModal(false)}
        onCreate={handleCreateOrganization}
      />
    </div>
  );
};

export default Layout;
